import axios from 'axios';
import { uniqBy, uniq } from 'lodash-es';
import { LocalStorage } from '@hotjar/insights-common/lib/storage-factory';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { getUserResources, USER_RESOURCES_QUERY_KEY } from '@hotjar/user-resources';
import { HotjarFilterOptionAll, HotjarFilterOptionWatched } from '../../../list/types';
import { getRecordingsSearchContext } from './getRecordingsSearchContext';
import { getStoredRecordingsFragment } from './getStoredRecordingsFragment';
import {
  API_ROOT_V3,
  RECORDINGS_FRAGMENT_STORAGE_KEY,
  ITEMS_PER_PAGE,
  SEARCH_CONTEXT_STORAGE_KEY,
  WATCH_RECORDINGS_IDS_LIST_STORAGE_KEY,
} from './constants';
import { selectPartialRecordingProps } from './selectPartialRecordingProps';
import { DEFAULT_HOTJAR_FILTERS } from '../../../../constants/hotjar-filters';
import { generateRequestFilters } from '../../../../utils/generateRequestFilters';
import { convertSortByV2ToV3 } from '../../../../features/RecordingsPage/utils/convertSortByV2ToV3';
import { getFiltersMergedWithRelevanceFilter } from '../../../../utils/getFiltersMergedWithRelevanceFilter';

import type { GetState, SetState, StoreApi } from 'zustand';
import type { Recording } from '@hotjar/webapp/features/Playbacks/types';
import type { ListContextSlice, PartialRecording, State } from '../../types';

export const createRecordingsListSlice: (
  set: SetState<State>,
  get: GetState<State>,
  api: StoreApi<State>,
) => ListContextSlice = (set, get) => ({
  hasMoreRecordings: true,
  hotjarFilters: { ...DEFAULT_HOTJAR_FILTERS },

  ...getRecordingsSearchContext(),
  ...getStoredRecordingsFragment(),

  getRecordingIndexById: (id: number): number => {
    const { lowerBound, recordingsList } = get();
    return recordingsList.findIndex((r) => r.id === id) + lowerBound;
  },

  getRecordingIdByIndex: (index: number): number => {
    const { lowerBound, recordingsList } = get();
    return recordingsList[index - lowerBound]?.id;
  },

  addRecordingsAtOffset: (recordings: PartialRecording[], offset: number) => {
    const { recordingsList, lowerBound } = get();
    const updatedList =
      offset < lowerBound ? [...recordings, ...recordingsList] : [...recordingsList, ...recordings];
    const cachedWatchedRecordingsIds =
      (LocalStorage.get(WATCH_RECORDINGS_IDS_LIST_STORAGE_KEY) as number[]) || [];
    const watchedRecordingsIds = updatedList.filter((r) => r.watched).map((r) => r.id);
    const stateFragment = {
      recordingsList: uniqBy(updatedList, (r) => r.id),
      lowerBound: offset >= 0 && offset < lowerBound ? offset : lowerBound,
      watchedRecordingsIdsList: uniq([...cachedWatchedRecordingsIds, ...watchedRecordingsIds]),
    };

    sessionStorage.setItem(RECORDINGS_FRAGMENT_STORAGE_KEY, JSON.stringify(stateFragment));
    set(stateFragment);
  },

  removeRecordingFromList: (id: number) => {
    const { rootRecording, recordingsList } = get();

    set({
      rootRecording: rootRecording?.id === id ? undefined : rootRecording,
      recordingsList: recordingsList.filter((r) => r.id === id),
    });
  },

  populateRootRecording: (recording: Recording) => {
    const { rootRecording } = get();
    if (!rootRecording) return;

    const context = JSON.parse(sessionStorage.getItem(SEARCH_CONTEXT_STORAGE_KEY) || '{}');
    context.rootRecording = selectPartialRecordingProps(recording);

    sessionStorage.setItem(SEARCH_CONTEXT_STORAGE_KEY, JSON.stringify(context));
    set({ rootRecording: context.rootRecording });
  },

  setWatchedRecordingsIdsList: (watchedRecordingsIdsList: number[]): void => {
    set({ watchedRecordingsIdsList });
  },

  fetchPreviousRecordingsPage: () => {
    const { lowerBound, fetchRecordings } = get();
    if (!lowerBound) {
      return;
    }

    fetchRecordings(lowerBound >= ITEMS_PER_PAGE ? lowerBound - ITEMS_PER_PAGE : 0);
  },

  fetchNextRecordingsPage: () => {
    const { recordingsList, lowerBound, hasMoreRecordings, rootRecording, fetchRecordings } = get();
    if (!hasMoreRecordings) {
      return;
    }

    // If we have a root recording, it can't count towards the offset because it's external to the list
    const offset = lowerBound + recordingsList.length;
    fetchRecordings(offset !== 0 && rootRecording ? offset - 1 : offset);
  },

  fetchRecordings: (offset: number) => {
    const {
      rootRecording,
      jsonFilters,
      hotjarFilters,
      sortBy,
      siteId,
      recordingsRequestTimeRange,
      playbacksOpener,
    } = get();
    const filters = generateRequestFilters(jsonFilters, recordingsRequestTimeRange);

    const postData = {
      global_filters: JSON.stringify(getFiltersMergedWithRelevanceFilter(filters, hotjarFilters)),
      ...(hotjarFilters.watched !== HotjarFilterOptionAll && {
        watched: hotjarFilters.watched === HotjarFilterOptionWatched,
      }),
      is_initial_load: false,
      sort_by: convertSortByV2ToV3(sortBy),
      source: playbacksOpener
        ? ['recordings-player', playbacksOpener].join('_')
        : 'recordings-player',
      tz_offset: -new Date().getTimezoneOffset(),
      size: ITEMS_PER_PAGE,
      offset,
      exclude_ids: rootRecording ? [rootRecording.id] : undefined,
    };

    axios.post(`${API_ROOT_V3}/${siteId}/recordings`, postData).then(({ data: res }) => {
      const { addRecordingsAtOffset } = get();

      if (res.total_hits <= offset + ITEMS_PER_PAGE) {
        set({ hasMoreRecordings: false });
      }
      const recordings = res.recordings.map((r: Recording) => selectPartialRecordingProps(r));
      if (offset === 0 && rootRecording) {
        recordings.unshift(rootRecording);
      }

      addRecordingsAtOffset(recordings, offset);
    });
  },

  /**
   * Temporary for FN-934, will not be necessary once we roll out v3
   * recordings to 100% of the customers
   */
  site: null,
  fetchSiteForUserId: async (userId: number) => {
    const { siteId } = get();

    // Fetch user resources to get the site
    const userResources = await queryClient.fetchQuery(
      USER_RESOURCES_QUERY_KEY,
      getUserResources.bind(null, userId),
    );

    const currentSite =
      userResources.sites.find((site) => site.id === siteId) || userResources.sites[0];

    set({ site: currentSite });
  },
});
